/**
 * Ask layer core: the contract (plain TypeScript types + constants). ZERO dependencies.
 *
 *            import: `shared/src/ask/`, `packages/ask-core/src/`, or simply `src/help/ask/` in a
 *            browser-only adoption. Everything in `core/` compiles under a DOM tsconfig and under
 *            a Node tsconfig alike: no `node:` imports, no framework, no validation library.
 * Run:       not run — imported.
 * EDIT:      CATALOG_LANGUAGES, CATALOG_AREAS, AREA_PREFIX, CATALOG_ROLES (marked below). Nothing
 *            else is configuration; the caps and the field names are the contract the validator
 *            (`validate.ts`), the editor schema (`catalog/schema.json`), the CI check and the panel
 *            all assume. The frontend template `templates/help/ask-api.ts` carries a copy of the
 *            response types — if you change a field name here, change it there.
 */

/**
 * The capability catalog — the assistant's only source of truth.
 *
 * One entry = one job-to-be-done = one retrieval unit (never chunked). Entries live as
 * markdown files with YAML frontmatter under `/catalog/<area>/<ID>.md`, are reviewed in PRs,
 * and are compiled into a generated module by `npm run catalog:build`. `validate.ts` is what
 * both the compiler and the CI gate validate against — `catalog/schema.json` is the editor-facing
 * mirror of the same rules.
 *
 * Plan B (no LLM) reads these entries and renders them verbatim as cards. Plan A would add a
 * generated answer grounded in the same entries; nothing here changes for that.
 */

// ---------------------------------------------------------------------------
// Languages
// ---------------------------------------------------------------------------

/**
 * The languages the catalog can be authored in. The first is the BASE: it lives in the entry's
 * top-level fields and is always required, so every entry answers something even when a
 * translation is missing. Every other language is a block under `i18n`.
 *
 * Adding a language is a data change: append the code here, add it to `schema.json`'s
 * `i18n.properties`, author the `i18n` blocks, and the search index and the panel pick it up.
 * No code path is keyed to a specific language.
 */
export const CATALOG_LANGUAGES = ['en', 'lt', 'lv'] as const; // base first; must equal HELP_LANGUAGES (registry.test.ts)
export type CatalogLanguage = (typeof CATALOG_LANGUAGES)[number];

/** The language the top-level (untranslated) fields are written in. */
export const CATALOG_BASE_LANGUAGE: CatalogLanguage = 'en';

// ---------------------------------------------------------------------------
// Entry vocabulary
// ---------------------------------------------------------------------------

/**
 * Areas mirror the app's navigation groups rather than the backend modules — the catalog is
 * organised the way a user thinks about the product, and `area` is a retrieval score boost
 * (never a filter: a cross-area question is legitimate).
 */
export const CATALOG_AREAS = [
  'bank-statements',
  'insurer-reports',
  'imported-invoices',
  'payment-plans',
  'client-balance',
  'payments',
  'reminders',
  'general',
] as const;
export type CatalogArea = (typeof CATALOG_AREAS)[number];

/**
 * The three-letter id prefix each area's entries carry (`ORD-001` lives in `catalog/orders/`).
 * `catalog-check` enforces the pairing; renaming a prefix renames every id in the area, and ids
 * are referenced by telemetry and the golden set — so pick these once.
 */
export const AREA_PREFIX = {
  'bank-statements': 'BNK',
  'insurer-reports': 'INS',
  'imported-invoices': 'IMP',
  'payment-plans': 'PPL',
  'client-balance': 'CLB',
  payments: 'PAY',
  reminders: 'REM',
  general: 'GEN',
} as const satisfies Record<CatalogArea, string>;

/** Authoring lifecycle. Only `published` entries are retrievable. */
export const CATALOG_STATUSES = ['draft', 'in-review', 'published', 'deprecated'] as const;
export type CatalogStatus = (typeof CATALOG_STATUSES)[number];

/**
 * The role vocabulary is the backend's own — the same strings your authorization layer
 * enforces — so an entry cannot name a role the platform does not have. Roles NEVER filter
 * retrieval — an entry the user lacks rights for still answers, stating the right as a
 * prerequisite. Hiding capabilities would recreate the discoverability problem the assistant
 * exists to solve.
 */
// PCT's server roles: PCT-Admins (`admin`); PCT-Limited and allowed GOS roles (`user`).
export const CATALOG_ROLES = ['user', 'admin'] as const;
export type CatalogRole = (typeof CATALOG_ROLES)[number];

/**
 * What the panel does with the entry's `anchor` once the destination screen has rendered.
 *
 * `flash` outlines the control and stops there. `open` presses it, because for a job that
 * *begins* in a dialog — "New order", "Add customer" — arriving on the list with the button
 * merely highlighted leaves the user one unexplained step short of what they asked for.
 *
 * Opting an entry in is a promise about the control it anchors: pressing it must only OPEN
 * something (a dialog, a wizard, a panel) and write nothing. A submit button, a row action or
 * anything destructive stays `flash` — the assistant may not act on a record on the user's
 * behalf.
 */
export const CATALOG_ANCHOR_ACTIONS = ['flash', 'open'] as const;
export type CatalogAnchorAction = (typeof CATALOG_ANCHOR_ACTIONS)[number];

/** What an entry that says nothing gets: point at the control, touch nothing. */
export const CATALOG_DEFAULT_ANCHOR_ACTION: CatalogAnchorAction = 'flash';

// ---------------------------------------------------------------------------
// Caps — enforced by validate.ts, mirrored in catalog/schema.json
// ---------------------------------------------------------------------------

export const CATALOG_JOB_MAX = 120;
export const CATALOG_DESCRIPTION_MAX = 1200;
export const CATALOG_PREREQUISITES_MAX = 600;
export const CATALOG_ALIASES_MAX = 40;
export const CATALOG_ALIAS_MAX = 120;
export const CATALOG_ANCHOR_MAX = 80;
export const CATALOG_SOURCE_REF_MAX = 200;
export const CATALOG_OWNER_MAX = 80;
export const CATALOG_ACTION_OPERATION_MAX = 120;
export const CATALOG_ACTION_REQUIRES_MAX = 60;
export const CATALOG_ACTION_CONFIRMATION_MAX = 300;
export const ASSISTANT_QUESTION_MAX = 300;
export const ASSISTANT_SCREEN_MAX = 200;
export const ASSISTANT_LIMIT_MIN = 1;
export const ASSISTANT_LIMIT_MAX = 10;
export const ASSISTANT_QUERY_ID_MAX = 64;
export const ASSISTANT_COMMENT_MAX = 1000;

// ---------------------------------------------------------------------------
// Entry
// ---------------------------------------------------------------------------

/** The fields a translation may carry. Everything else (ids, links, roles) is language-free. */
export interface CatalogTranslation {
  job: string;
  description: string;
  prerequisites?: string;
  /**
   * What a user of THIS language would call the job. The load-bearing field in Plan B:
   * with no semantic matching, aliases are how "wie lege ich eine bestellung an" reaches an
   * entry titled "Create a new order".
   */
  aliases?: string[];
}

/** Translations by language. The base language never appears here (validate.ts refuses it). */
export type CatalogEntryI18n = Partial<Record<CatalogLanguage, CatalogTranslation>>;

/**
 * Phase 2 seam (not implemented): presence makes an entry action-capable. Kept in the
 * contract from day one so adding an action is a data change, never a schema migration.
 */
export interface CatalogAction {
  operation: string;
  requires?: string;
  reversible: boolean;
  confirmation: string;
}

export interface CatalogEntry {
  /** Area prefix + number, stable forever (`ORD-001`). Renaming one breaks telemetry history. */
  id: string;
  area: CatalogArea;
  /** Verb-first, user language, ≤ 10 words. The primary retrieval field. */
  job: string;
  /** 2–4 sentences, active voice, no developer vocabulary. Rendered verbatim to the user. */
  description: string;
  /** Stated as data, not buried in prose — the panel shows it as its own line. */
  prerequisites?: string;
  aliases?: string[];
  /** Roles for whom this is a normal part of the job. Rank boost only. */
  roles: CatalogRole[];
  /**
   * Where the panel can take the user, as a route the frontend router knows — validated by
   * `catalog:check` against the router file. **Literal only**: the panel navigates here, and
   * no one can navigate to `/orders/:id`. When the job is done on a record's page, link to
   * the list and put the real route in `anchor_route`.
   */
  deeplink: string;
  /**
   * Optional `data-help` anchor for the control the job happens at. When the anchor is on the
   * deep-linked page the panel flashes it after navigating — the capability the page-guide
   * system already has and a plain deep link does not. `catalog:check` validates that the
   * page named by `anchor_route` actually renders it.
   */
  anchor?: string;
  /**
   * The route whose page renders `anchor`, when that is NOT the deep-linked page. Defaults
   * to `deeplink`.
   *
   * Half a catalog documents jobs done on a record's own page — "cancel this order", "add a
   * note to a customer" — and `/orders/:id` cannot be deep-linked, so those entries link to
   * the list. Without this field the panel had no way to know the difference and promised to
   * point at a control the list page never renders: "Show me the control" polled for four
   * seconds and then did nothing at all. Stated, the same entry works both ways — flashed in
   * place when the user is already on the record, and honest about the extra step when they
   * are not.
   *
   * `:param` segments are allowed here precisely because this route is matched, never
   * navigated to.
   */
  anchor_route?: string;
  /**
   * The `data-help` anchor of the control that OPENS the surface `anchor` lives inside — a
   * dialog, a drawer, a wizard step.
   *
   * `anchor_route` answers "which page", which turned out to be only half the question. A form
   * is not a route: a field in the "New order" dialog is on `/orders` in the sense that matters
   * to a router and nowhere at all in the sense that matters to a user. The panel pointed at
   * it anyway, polled a list page for four seconds and gave up in silence — the same "button
   * that does nothing" `anchor_route` was added to kill, one layer down.
   *
   * Named, the panel can take the step itself: press this control, wait for the surface, then
   * flash the anchor inside it. It presses only when there is exactly ONE such control on
   * screen — a row action exists once per row, and which record the user meant is never the
   * panel's call, so an ambiguous opener is flashed instead. Either way something visible
   * happens, which is the whole point.
   *
   * Same promise as `anchor_action: open`: the named control must OPEN something and write
   * nothing. `catalog:check` enforces the rest — that the anchor really is behind a surface,
   * and that this opener is on `anchor_route` without being behind a surface itself.
   */
  anchor_surface?: string;
  /**
   * Whether the panel flashes that anchor (default) or presses it. `open` is only for a
   * control that opens a form and writes nothing — see `CATALOG_ANCHOR_ACTIONS`.
   */
  anchor_action?: CatalogAnchorAction;
  /** Where the entry came from — a page guide, a manual chapter, a spec. Keeps the corpora honest. */
  source_ref?: string;
  status: CatalogStatus;
  owner: string;
  /**
   * Translations. The base language lives in the top-level fields, so this map holds every
   * OTHER language; a missing translation falls back to the base and the panel says so.
   */
  i18n?: CatalogEntryI18n;
  action?: CatalogAction;
}

// ---------------------------------------------------------------------------
// Search API — the wire contract of POST /search and POST /feedback
// ---------------------------------------------------------------------------

export interface AssistantSearchRequest {
  question: string;
  /** The route the user is on. An area boost, never a filter. */
  screen?: string;
  language?: CatalogLanguage;
  /** How many cards to return. The panel asks for 4; the eval harness asks for more. */
  limit?: number;
}

/**
 * Feedback is the miss-log's other half: a match that was shown but did not help.
 *
 * Two INDEPENDENT signals, and keeping them independent is the point. `helpful` is the thumbs,
 * which most users never click. `deeplinkFollowed` is the beacon the panel fires when someone
 * acts on an answer — far more common, and the stronger evidence. They were once sent together
 * (a followed link reported itself as `helpful: true`), which inflated the thumbs-up rate by
 * every navigation and made "how many people said this helped" unanswerable.
 *
 * Either may arrive alone; at least one must (validate.ts enforces it).
 */
export interface AssistantFeedback {
  queryId: string;
  /** The thumbs. Absent on a deep-link beacon — a follow is not a verdict. */
  helpful?: boolean;
  comment?: string;
  /** The user acted on an answer: navigated to the screen, or had its control opened. */
  deeplinkFollowed?: boolean;
}

/** Former name of `AssistantFeedback`, kept so older call sites read unchanged. */
export type AssistantFeedbackInput = AssistantFeedback;

/** One card as the panel renders it — already resolved into the requested language. */
export interface AssistantResultCard {
  id: string;
  area: CatalogArea;
  job: string;
  description: string;
  prerequisites: string | null;
  roles: CatalogRole[];
  deeplink: string;
  anchor: string | null;
  /**
   * The route pattern whose page renders `anchor`. Always set — it falls back to `deeplink`,
   * so "same page" reads as `anchorRoute === deeplink` rather than as a null check. May carry
   * `:param` segments: the panel matches it against the current path, it never navigates to it.
   */
  anchorRoute: string;
  /**
   * The control that opens the surface `anchor` sits inside — a dialog, a drawer, a wizard step
   * — or null when the anchor is on the page itself. The panel presses it on the way to the
   * anchor, so "the field is in a form" costs the user no extra step and, more importantly,
   * never costs them a button that silently does nothing.
   */
  anchorSurface: string | null;
  /** Flash that anchor, or press it. Always set — an entry that says nothing gets `flash`. */
  anchorAction: CatalogAnchorAction;
  score: number;
  /** The language this card's text is actually in — may differ from the one asked for. */
  language: CatalogLanguage;
  /** True when the requested language had no translation and the base language is shown. */
  fallback: boolean;
}

/**
 * The answer seam. Plan B only ever returns `cards`; Plan A adds a `generated` kind carrying
 * `text` alongside the same entries, and the panel keeps the `cards` branch as its fallback.
 */
export interface AssistantAnswer {
  kind: 'cards' | 'generated';
  entries: AssistantResultCard[];
  text?: string;
}

export interface AssistantSearchResponse {
  /** Correlates the search with a later feedback or deep-link beacon. */
  queryId: string;
  matchFound: boolean;
  answer: AssistantAnswer;
  /** Rendered by the panel when `matchFound` is false. Fixed template, never generated. */
  notFound?: { message: string; suggestions: string[] };
  language: CatalogLanguage;
}

/** What `POST /feedback` answers with (HTTP 202). */
export interface AssistantFeedbackResponse {
  recorded: boolean;
}

// ---------------------------------------------------------------------------
// Who is asking — resolved by the adapter from its auth, never from the request body
// ---------------------------------------------------------------------------

/**
 * The caller, as the adapter knows them. `id` collapses one person's keystroke burst into one
 * miss-log row; `roles` rank-boost the results and never hide an entry. Both are optional in
 * effect: the panel is behind auth, so a missing principal is a fallback, not a mode.
 */
export interface Principal {
  id?: string;
  roles: readonly string[];
}

/** Your tenant / organisation scope, if the app has one. Stamped on telemetry only. */
export type TenantId = string | number | null;
